import { Component } from 'react'
import styled from 'styled-components'
import { X, RefreshCw, Plus } from 'lucide-react'
import CalendarService from '../../Services/CalendarService'
import CalendarView from './CalendarView'
import EventList from './EventList'
import EventFormModal from './EventFormModal'
import swaller from '../Tools/Swaller'

export default class CalendarModal extends Component {
  constructor (props) {
    super(props)
    this.state = {
      events: [],
      sharedEvents: [],
      selectedDate: new Date(),
      loading: false,
      refreshing: false,
      showEventForm: false,
      editingEvent: null
    }
  }

  async componentDidMount () {
    await this.fetchEvents()

    // Auto-refresh ogni 5 minuti se modal aperto
    this.refreshInterval = setInterval(() => this.fetchEvents(true), 5 * 60 * 1000)
  }

  componentWillUnmount () {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  }

  fetchEvents = async (silent = false) => {
    if (!silent) {
      this.setState({ loading: true })
    } else {
      this.setState({ refreshing: true })
    }

    try {
      // Fetch eventi dal database locale
      // Range esteso per visualizzare eventi ricorrenti (cedole, costi)
      const pastDate = new Date()
      pastDate.setMonth(pastDate.getMonth() - 12) // 12 mesi nel passato

      const futureDate = new Date()
      futureDate.setMonth(futureDate.getMonth() + 24) // 24 mesi nel futuro

      const [events, sharedEvents] = await Promise.all([
        CalendarService.getEvents(pastDate.toISOString(), futureDate.toISOString()),
        CalendarService.getSharedEvents(pastDate.toISOString(), futureDate.toISOString())
      ])

      this.setState({
        events: events || [],
        sharedEvents: sharedEvents || []
      })
    } catch (error) {
      console.error('Error fetching events:', error)
      if (!silent) {
        swaller.error('Errore nel caricamento degli eventi')
      }
    } finally {
      this.setState({ loading: false, refreshing: false })
    }
  }

  handleRefresh = async () => {
    await this.fetchEvents()
  }

  handleDateSelect = (date) => {
    this.setState({ selectedDate: date })
  }

  handleClose = (e) => {
    if (e.target === e.currentTarget) {
      this.props.onClose()
    }
  }

  handleCreateEvent = () => {
    this.setState({ showEventForm: true, editingEvent: null })
  }

  handleEditEvent = (event) => {
    this.setState({ showEventForm: true, editingEvent: event })
  }

  handleDeleteEvent = async (event) => {
    const confirmed = window.confirm(`Sei sicuro di voler eliminare l'evento "${event.title}"?`)
    if (!confirmed) return

    try {
      await CalendarService.deleteEvent(event._id)
      await this.fetchEvents()
    } catch (error) {
      console.error('Error deleting event:', error)
      swaller.error('Errore nell\'eliminazione dell\'evento')
    }
  }

  handleEventFormClose = () => {
    this.setState({ showEventForm: false, editingEvent: null })
  }

  handleEventFormSuccess = async () => {
    await this.fetchEvents()
  }

  handleToggleComplete = async (event) => {
    try {
      const updatedEvent = await CalendarService.toggleEventComplete(event._id)

      // Aggiorna l'evento nella lista locale
      this.setState(prevState => ({
        events: prevState.events.map(e =>
          e._id === updatedEvent._id ? updatedEvent : e
        ),
        sharedEvents: prevState.sharedEvents.map(e =>
          e._id === updatedEvent._id ? updatedEvent : e
        )
      }))
    } catch (error) {
      // Mostra errore (es. toast notification)
      console.error('Errore toggle completamento:', error.message)
      swaller.error(error.message || 'Errore durante l\'operazione')
    }
  }

  render () {
    const { onClose } = this.props
    const { events, sharedEvents, selectedDate, loading, refreshing, showEventForm, editingEvent } = this.state

    // Merge eventi + condivisi
    const allEvents = [...events]

    // Marca eventi condivisi e aggiungi quelli non presenti
    sharedEvents.forEach(sharedEvent => {
      const existingEvent = allEvents.find(e => e._id === sharedEvent._id)
      if (existingEvent) {
        // Evento già presente - marca come condiviso
        existingEvent._isShared = true
        existingEvent._createdBy = sharedEvent.createdBy
        existingEvent._sharedWith = sharedEvent.sharedWith
      } else {
        // Evento non presente - aggiungilo
        allEvents.push({
          ...sharedEvent,
          _isShared: true
        })
      }
    })

    // Filtra eventi per data selezionata
    const eventsForSelectedDate = CalendarService.filterEventsByDate(allEvents, selectedDate)

    return (
      <ModalOverlay visible onClick={this.handleClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <Title>Calendario</Title>
            <HeaderActions>
              <RefreshButton
                onClick={this.handleRefresh}
                disabled={refreshing}
                title='Aggiorna eventi'
              >
                <RefreshCw size={18} className={refreshing ? 'spinning' : ''} />
              </RefreshButton>
              <CloseButton onClick={onClose} title='Chiudi'>
                <X size={20} />
              </CloseButton>
            </HeaderActions>
          </ModalHeader>

          <ModalBody>
            {loading
              ? (
                <CalendarLayout>
                  <CalendarSection>
                    <SkeletonCalendarHeader>
                      <LoadingSkeleton height='28px' width='28px' />
                      <LoadingSkeleton height='24px' width='150px' />
                      <LoadingSkeleton height='28px' width='28px' />
                    </SkeletonCalendarHeader>
                    <SkeletonWeekDays>
                      {[1, 2, 3, 4, 5, 6, 7].map(i => (
                        <LoadingSkeleton key={i} height='20px' width='40px' />
                      ))}
                    </SkeletonWeekDays>
                    <SkeletonDaysGrid>
                      {[...Array(35)].map((_, i) => (
                        <LoadingSkeleton key={i} height='40px' />
                      ))}
                    </SkeletonDaysGrid>
                  </CalendarSection>

                  <EventsSection>
                    <SkeletonEventsHeader>
                      <LoadingSkeleton height='24px' width='200px' />
                      <LoadingSkeleton height='24px' width='30px' />
                    </SkeletonEventsHeader>
                    <SkeletonEventsList>
                      {[1, 2, 3, 4].map(i => (
                        <SkeletonEventCard key={i}>
                          <LoadingSkeleton height='100%' width='4px' style={{ borderRadius: 0 }} />
                          <SkeletonEventContent>
                            <LoadingSkeleton height='16px' width='120px' style={{ marginBottom: '0.5rem' }} />
                            <LoadingSkeleton height='20px' width='80%' style={{ marginBottom: '0.5rem' }} />
                            <LoadingSkeleton height='14px' width='60%' />
                          </SkeletonEventContent>
                        </SkeletonEventCard>
                      ))}
                    </SkeletonEventsList>
                  </EventsSection>
                </CalendarLayout>
                )
              : (
                <CalendarLayout>
                  <CalendarSection>
                    <CalendarView
                      selectedDate={selectedDate}
                      events={allEvents}
                      onDateSelect={this.handleDateSelect}
                    />
                  </CalendarSection>

                  <EventsSection>
                    <EventsHeader>
                      <EventsTitle>
                        {selectedDate.toLocaleDateString('it-IT', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long'
                        })}
                      </EventsTitle>
                      <EventCount>{eventsForSelectedDate.length}</EventCount>
                    </EventsHeader>

                    <EventList
                      events={eventsForSelectedDate}
                      selectedDate={selectedDate}
                      onEditEvent={this.handleEditEvent}
                      onDeleteEvent={this.handleDeleteEvent}
                      onToggleComplete={this.handleToggleComplete}
                    />
                  </EventsSection>
                </CalendarLayout>
                )}
          </ModalBody>

          {!loading && (
            <ModalFooter>
              <CreateButton onClick={this.handleCreateEvent}>
                <Plus size={18} />
                <span>Nuovo Evento</span>
              </CreateButton>
            </ModalFooter>
          )}

          {showEventForm && (
            <EventFormModal
              event={editingEvent}
              selectedDate={selectedDate}
              onClose={this.handleEventFormClose}
              onSuccess={this.handleEventFormSuccess}
            />
          )}
        </ModalContainer>
      </ModalOverlay>
    )
  }
}

// ============ STYLED COMPONENTS ============

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.3)'};
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.2s ease;
`

const ModalContainer = styled.div`
  background: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.radius.card};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 95%;
    max-height: 95vh;
  }
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.medium};
`

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.button};
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 0.875rem;
  }

  &.spinning svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.background.hover};
    border-color: ${props => props.theme.colors.border.strong};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text.tertiary};
  cursor: pointer;
  border-radius: ${props => props.theme.radius.button};
  transition: all 0.2s ease;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background: ${props => props.theme.colors.background.hover};
    color: ${props => props.theme.colors.text.primary};
  }
`

const ModalBody = styled.div`
  overflow: hidden;
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`

// Skeleton Loader Components
const LoadingSkeleton = styled.div`
  background: linear-gradient(90deg,
    ${props => props.theme.colors.background.secondary} 25%,
    ${props => props.theme.colors.background.hover} 50%,
    ${props => props.theme.colors.background.secondary} 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: ${props => props.theme.radius.input};
  height: ${props => props.height || '20px'};
  width: ${props => props.width || '100%'};

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`

const SkeletonCalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const SkeletonWeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`

const SkeletonDaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
`

const SkeletonEventsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const SkeletonEventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const SkeletonEventCard = styled.div`
  display: flex;
  background: ${props => props.theme.colors.background.hover};
  border-radius: ${props => props.theme.radius.input};
  overflow: hidden;
  height: 100px;
`

const SkeletonEventContent = styled.div`
  flex: 1;
  padding: 0.875rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const CalendarLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  flex: 1;
  overflow: hidden;
  min-height: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }
`

const CalendarSection = styled.div`
  /* Calendar view - rimane fisso senza scroll */
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const EventsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.5rem;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border.medium};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.border.strong};
  }
`

const EventsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const EventsTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
  text-transform: capitalize;
`

const EventCount = styled.span`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.muted};
  background: ${props => props.theme.colors.background.hover};
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
`

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem 1.75rem;
  border-top: 1px solid ${props => props.theme.colors.border.medium};
  gap: 0.75rem;
`

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: ${props => props.theme.colors.semantic.primary};
  color: ${props => props.theme.colors.text.inverse};
  border: none;
  border-radius: ${props => props.theme.radius.button};
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: ${props => props.theme.transitions.button};

  svg {
    font-size: 0.875rem;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    span {
      display: none;
    }
    padding: 0.625rem;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    justify-content: center;
  }
`
